import React, { useContext, useMemo, useRef, useState } from 'react';
import { useSectionBlockContext } from '../contexts/block';
import { usePageContext } from '../contexts/page';
import { store } from '../redux';
import { actions } from '../redux/slices/page';
import { findSection } from '../utils/page/section';
import { sameRegistration } from '../utils/registration';
import { useContentManagement } from './use-content-management';
import { EditingContext } from '../contexts/editing';
const NO_PROPS = Object.freeze({});
export function useField(name, type, options) {
    const { jaenPage } = usePageContext();
    if (!jaenPage.id) {
        throw new Error('JaenPage id is undefined! connectField must be used within a JaenPage');
    }
    const SectionBlockContext = useSectionBlockContext();
    function getPageField(page) {
        if (page) {
            let fields;
            const path = options?.block?.path || SectionBlockContext?.path;
            const blockId = options?.block?.id || SectionBlockContext?.id;
            if (path && !options?.isPageField) {
                fields = findSection(page.sections || [], path)?.items.find(({ id }) => id === blockId)?.jaenFields;
            }
            else {
                fields = page.jaenFields;
            }
            return fields?.[type]?.[name];
        }
        return undefined;
    }
    const getField = () => {
        const state = store.getState();
        const page = state.page.pages.nodes[jaenPage.id];
        if (page) {
            return getPageField(page);
        }
        return undefined;
    };
    const getStaticField = () => {
        const page = jaenPage;
        if (page) {
            return getPageField(page);
        }
        return undefined;
    };
    const readFieldRef = useRef(getField);
    readFieldRef.current = getField;
    const fieldKey = [
        jaenPage.id,
        type,
        name,
        options?.isPageField ? 'page' : '',
        JSON.stringify(options?.block?.path ?? SectionBlockContext?.path ?? null),
        String(options?.block?.id ?? SectionBlockContext?.id ?? '')
    ].join('|');
    const sectionKey = SectionBlockContext
        ? JSON.stringify([SectionBlockContext.path, SectionBlockContext.id])
        : '';
    const section = useMemo(() => SectionBlockContext
        ? {
            path: SectionBlockContext.path,
            id: SectionBlockContext.id
        }
        : undefined, [sectionKey]);
    const [field, setField] = useState(getField);
    React.useEffect(() => {
        const next = readFieldRef.current();
        setField(current => current === next || JSON.stringify(current) === JSON.stringify(next)
            ? current
            : next);
    }, [fieldKey]);
    React.useEffect(() => {
        let last = readFieldRef.current();
        let lastSerialised = JSON.stringify(last);
        const unsubscribe = store.subscribe(() => {
            const next = readFieldRef.current();
            if (next === last) {
                return;
            }
            const serialised = JSON.stringify(next);
            last = next;
            if (serialised === lastSerialised) {
                return;
            }
            lastSerialised = serialised;
            setField(next);
        });
        return () => {
            unsubscribe();
        };
    }, [fieldKey]);
    const staticField = getStaticField();
    const editingContext = useContext(EditingContext);
    const contentManagement = useContentManagement();
    const isEditing = editingContext
        ? editingContext.isEditing
        : contentManagement.isEditing;
    const props = useMemo(() => {
        return field?.props || staticField?.props || NO_PROPS;
    }, [field, staticField]);
    const write = React.useCallback((newValue) => {
        store.dispatch(actions.field_write({
            pageId: jaenPage.id,
            section,
            fieldType: type,
            fieldName: name,
            value: newValue,
            props
        }));
    }, [jaenPage.id, section, type, name, props]);
    const register = React.useCallback((registerProps) => {
        const current = readFieldRef.current();
        if (current && sameRegistration(current.props, registerProps)) {
            return;
        }
        store.dispatch(actions.field_register({
            pageId: jaenPage.id,
            fieldType: type,
            fieldName: name,
            section,
            props: registerProps
        }));
    }, [jaenPage.id, section, type, name]);
    return {
        value: field?.value,
        staticValue: staticField?.value,
        props,
        jaenPageId: jaenPage.id,
        isEditing,
        SectionBlockContext,
        write,
        register
    };
}
